import { forwardRef } from 'react';
import './CloseButton.css';

export interface CloseButtonProps {
  /** Click handler. Required — the button has no other purpose. */
  onClick: () => void;
  /**
   * Accessible label announced by screen readers. Default: "Close".
   * Provide a more specific label where the button's context isn't obvious
   * from visual surroundings — e.g., "Close compose email".
   */
  label?: string;
  /** Extra class names appended to the button's root. */
  className?: string;
  /** Native button type. Defaults to `button` to prevent form submission. */
  type?: 'button' | 'submit' | 'reset';
}

/**
 * Shared close (X) button with a 32×32 visual target, 44×44 hit area (via
 * ::before pseudo-element), focus-visible outline, and proper screen reader
 * support. Replaces duplicated inline close buttons in ComposeEmailModal
 * and EventListModule which had WCAG 2.1 violations (no :focus-visible,
 * only `title` attribute without `aria-label`).
 */
export const CloseButton = forwardRef<HTMLButtonElement, CloseButtonProps>(
  function CloseButton({ onClick, label = 'Close', className = '', type = 'button' }, ref) {
    return (
      <button
        ref={ref}
        type={type}
        className={`xv-close-button ${className}`.trim()}
        onClick={onClick}
        aria-label={label}
        title={label}
      >
        <svg
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <line x1="18" y1="6" x2="6" y2="18" />
          <line x1="6" y1="6" x2="18" y2="18" />
        </svg>
      </button>
    );
  },
);
